import React, { useReducer, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Margin } from 'styled-components-spacing';
import clearErrors from 'helpers/clearErrors';
import {
  PageContent,
  Container,
  Row,
  Col,
  Field,
  Heading,
  Button,
  Datepicker,
  Select,
} from 'components';
import reducer, { defaultState, updateFieldCreator, setEditDataCreator } from './reducer';
import { getAssessorUsersOptions, getUsersOptions } from '../../../../../api/services/user';
import {
  updateMaCisCreator,
  setMaCisEditErrorsCreator,
} from '../../../../../store/actionsCreators';
import DebouncedSelect from '../../../../../components/DebounceSelect/DebounceSelect';
import FileInput from '../../../../../components/FileInput/FileInput';
import moment from 'moment';
import { roleOptions, signatureTypeOptions } from '../../../../../shared/Cis/CisOptions';

const EditAssessors = ({ errors, updateAssessor, removeError, assessor }) => {
  const { t } = useTranslation();
  const [state, dispatch] = useReducer(reducer, defaultState);
  const history = useHistory();

  useEffect(() => {
    if (assessor) {
      dispatch(setEditDataCreator(assessor));
    }
  }, [assessor]);

  const handleChangeSelect = (name, errorName) => (option) => {
    dispatch(updateFieldCreator({ name, value: option?.value }));
    if (errors[errorName]) removeError(clearErrors(errors, errorName));
  };

  const handleChangeFileNative = (name) => (e) => {
    dispatch(
      updateFieldCreator({
        name,
        dataType: 'file',
        value: e.target.files && e.target.files.length ? e.target.files[0] : null,
      }),
    );
  };

  const handleClearFileNative = (name) => () => {
    dispatch(
      updateFieldCreator({
        name,
        dataType: 'file',
        value: null,
      }),
    );
  };

  const handleChangeCustom = (name) => (value) => {
    dispatch(updateFieldCreator({ name, value }));
  };

  const submit = () => {
    const formData = new FormData();
    formData.append('user', state.user.id);
    if (state.dateFrom) {
      formData.append('dateFrom', moment(state.dateFrom).format('YYYY-MM-DD'));
    }
    if (state.signature && state.signatureType === 'H') {
      formData.append('signature', state.signature);
    }
    formData.append('signatureType', state.signatureType);
    formData.append('role', state.role);
    updateAssessor(state.id, formData, history);
  };

  return (
    <PageContent>
      <Container>
        <form>
          <Margin bottom="24">
            <Heading size="h5">{t('page.user.cis.assessors.editAssessor')}</Heading>
          </Margin>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="user"
                  label={t('page.user.cis.assessors.user')}
                  error={errors.user}
                  required
                >
                  <DebouncedSelect
                    id="user"
                    value={state.user?.id}
                    onChange={handleChangeSelect('user', 'user')}
                    placeholder={t('page.user.cis.assessors.userPlaceholder')}
                    fetchOptions={getAssessorUsersOptions}
                    intialSelectedOption={state.selectedUser}
                    disabled={true}
                    isClearable={false}
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="dateFrom"
                  label={t('page.user.cis.assessors.dateFrom')}
                  error={errors.dateFrom}
                >
                  <Datepicker
                    id="dateFrom"
                    type="text"
                    small
                    placeholderText={t('page.user.cis.assessors.dateFromPlaceholder')}
                    selected={state.dateFrom}
                    onChange={handleChangeCustom('dateFrom', 'dateFrom')}
                    dateFormat="yyyy-MM-dd"
                    showYearDropdown
                    showMonthDropdown
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="signatureType"
                  label={t('page.user.cis.assessors.signatureType')}
                  required
                  error={errors.signatureType}
                >
                  <Select
                    id="signatureType"
                    type="text"
                    placeholder={t('page.user.cis.assessors.signatureTypePlaceholder')}
                    value={
                      signatureTypeOptions.find((option) => option.value === state.signatureType) ||
                      ''
                    }
                    options={signatureTypeOptions}
                    onChange={handleChangeSelect('signatureType', 'signatureType')}
                  />
                </Field>
              </Margin>
            </Col>
          </Row>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="signature"
                  label={t('page.user.cis.assessors.signature')}
                  error={errors.signature}
                  disabled={state.signatureType !== 'H'}
                  required={state.signatureType === 'H'}
                  description={t('global.uploadPngInfo')}
                >
                  <FileInput
                    clear={handleClearFileNative('signature')}
                    onChange={handleChangeFileNative('signature')}
                    placeholder="Add a file"
                    fileName={
                      state.signature ? state.signature.name || state.signature.fileName : ''
                    }
                    disabled={state.signatureType !== 'H'}
                    accept=".png"
                  />
                </Field>
              </Margin>
            </Col>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom="20">
                <Field
                  id="role"
                  label={t('page.user.cis.assessors.role')}
                  required
                  error={errors.role}
                >
                  <Select
                    id="role"
                    type="text"
                    placeholder={t('page.user.cis.assessors.rolePlaceholder')}
                    value={roleOptions.find((option) => option.value === state.role) || ''}
                    options={roleOptions}
                    onChange={handleChangeSelect('role', 'role')}
                  />
                </Field>
              </Margin>
            </Col>
          </Row>
          <Row>
            <Col size={{ xl: 1 / 3 }}>
              <Margin bottom={{ xs: 12, md: 0 }}>
                <Button type="button" onClick={submit}>
                  {t('global.saveChangesButton')}
                </Button>
              </Margin>
            </Col>
          </Row>
        </form>
      </Container>
    </PageContent>
  );
};

EditAssessors.propTypes = {
  errors: PropTypes.object,
  updateAssessor: PropTypes.func.isRequired,
  removeError: PropTypes.func.isRequired,
  assessor: PropTypes.object,
};

EditAssessors.defaultProps = {
  errors: {},
  assessor: {},
};

const mapStateToProps = ({ ma }) => ({
  errors: ma.cis.assessors.editErrors || {},
  assessor: ma.cis.assessors.profile || {},
});

const mapDispatchToProps = (dispatch) => ({
  updateAssessor: (id, params, history) =>
    dispatch(updateMaCisCreator(id, 'assessors', params, history)),
  removeError: (errors) => dispatch(setMaCisEditErrorsCreator('assessors', errors)),
});

export default connect(mapStateToProps, mapDispatchToProps)(EditAssessors);
